import { ZodIssueCode, z } from 'zod';

import {
    ADDRESS_TYPE,
    HelpAssistance,
    PoliticalParty,
    RegistrationReason,
    VALIDATIONS,
} from './constants/voterRegistrationConstants';
import { APHABETIC_REGEX, CITY_REGEX } from '@utils/validators/regex';

function validateValues(data: any, ctx: any) {
    if (data.primaryPhone && data.primaryPhone.trim() !== '') {
        if (!new RegExp(`^\\d{${VALIDATIONS.PHONE.max}}$`).test(data.primaryPhone)) {
            ctx.addIssue({
                path: ['primaryPhone'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.phone.invalid',
            });
        }
    }
}

function validateRegistrationReason(reasons: RegistrationReason[], ctx: any) {
    const hasNew = reasons.includes(RegistrationReason.NEW_REGISTRATION);
    const changeReasons = [
        RegistrationReason.CHANGE_OF_NAME,
        RegistrationReason.CHANGE_OF_ADDRESS,
        RegistrationReason.CHANGE_OF_PARTY,
    ];
    const hasChange = reasons.some(r => changeReasons.includes(r));
    const hasFederal = reasons.includes(RegistrationReason.FEDERAL_VOTER);

    if (!hasNew && !hasChange && !hasFederal) {
        ctx.addIssue({
            path: ['registrationReason'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.registrationReason.required',
        });
    }

    if (hasNew && hasChange) {
        ctx.addIssue({
            path: ['registrationReason'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.registrationReason.newNoChange',
        });
    }

    if (!hasNew && hasChange && !reasons.some(r => changeReasons.includes(r))) {
        ctx.addIssue({
            path: ['registrationReason'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.registrationReason.changeRequired',
        });
    }

    if (hasFederal && hasNew && hasChange) {
        ctx.addIssue({
            path: ['registrationReason'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.registrationReason.federalNewNoChange',
        });
    }
}

function identificationValidation(identification: any, ctx: any) {
    const hasDL = !!identification.driverLicenseOrPennDotId && identification.driverLicenseOrPennDotId.trim() !== '';
    const hasSSN = !!identification.last4digitsSSN && identification.last4digitsSSN.trim() !== '';
    const hasNoId = !!identification.driverLicenseOrSSNNotAvailable;

    if (hasDL) {
        if (
            identification.driverLicenseOrPennDotId.length !== VALIDATIONS.DRIVER_LICENSE.max ||
            !new RegExp(`^\\d{${VALIDATIONS.DRIVER_LICENSE.max}}$`).test(identification.driverLicenseOrPennDotId)
        ) {
            ctx.addIssue({
                path: ['identification', 'driverLicenseOrPennDotId'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.identification.invalidDL',
            });
        }
    }

    if (hasSSN) {
        if (
            identification.last4digitsSSN.length !== VALIDATIONS.SSN4.max ||
            !new RegExp(`^\\d{${VALIDATIONS.SSN4.max}}$`).test(identification.last4digitsSSN)
        ) {
            ctx.addIssue({
                path: ['identification', 'last4digitsSSN'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.identification.invalidSSN4',
            });
        }
    }

    if (!hasDL && !hasSSN && !hasNoId) {
        ctx.addIssue({
            path: ['identification', 'driverLicenseOrSSNNotAvailable'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.identification.atLeastOneRequired',
        });
    }
}

function politicalPartyValidation(
    politicalParty: string | undefined | null,
    otherPoliticalParty: string | undefined,
    ctx: any,
) {

    if (politicalParty && politicalParty === PoliticalParty.OTHER) {
        if (!otherPoliticalParty || !otherPoliticalParty.trim()) {
            ctx.addIssue({
                path: ['otherPoliticalParty'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.politicalParty.otherRequired',
            });
        }
    }

    if (!politicalParty) {
        ctx.addIssue({
            path: ['politicalParty'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.politicalParty.required',
        });
    }
}

function votingAssistanceValidation(votingAssistance: any, ctx: any) {

    if (votingAssistance.helpRequired) {
        if (!votingAssistance.kindOfAssistance || !votingAssistance.kindOfAssistance.trim()) {
            ctx.addIssue({
                path: ['votingAssistance', 'kindOfAssistance'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingAssistance.kindRequired',
            });
        }

        if (votingAssistance.kindOfAssistance === HelpAssistance.LimitedEnglish) {
            if (!votingAssistance.preferredLanguage || !votingAssistance.preferredLanguage.trim()) {
                ctx.addIssue({
                    path: ['votingAssistance', 'preferredLanguage'],
                    code: ZodIssueCode.custom,
                    message: 'voterRegistration.form.error.votingAssistance.languageRequired',
                });
            }
        }
    }

    if (votingAssistance.preferredLanguage && !APHABETIC_REGEX.test(votingAssistance.preferredLanguage)) {
        ctx.addIssue({
            path: ['votingAssistance', 'preferredLanguage'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }
}

function votingInformationChangedValidation(votingInformationChanged: any, registrationReason: any[], ctx: any) {
    const changeOfName = registrationReason?.includes(RegistrationReason.CHANGE_OF_NAME);
    const changeOfAddress = registrationReason?.includes(RegistrationReason.CHANGE_OF_ADDRESS);

    if (changeOfName) {
        if (!votingInformationChanged.previousFirstName || !votingInformationChanged.previousFirstName.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'previousFirstName'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousFirstNameRequired',
            });
        }
        if (!votingInformationChanged.previousLastName || !votingInformationChanged.previousLastName.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'previousLastName'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousLastNameRequired',
            });
        }
    }
    if (changeOfAddress) {
        if (!votingInformationChanged.address || !votingInformationChanged.address.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'address'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousAddressRequired',
            });
        }
        if (!votingInformationChanged.city || !votingInformationChanged.city.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'city'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousCityRequired',
            });
        }
        if (!votingInformationChanged.state || !votingInformationChanged.state.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'state'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousStateRequired',
            });
        }
        if (!votingInformationChanged.zip || !votingInformationChanged.zip.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'zip'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousZipRequired',
            });
        }
        if (!votingInformationChanged.county || !votingInformationChanged.county.trim()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'county'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.previousCountyRequired',
            });
        }
    }

    if (votingInformationChanged.year) {
        if (!/^\d{4}$/.test(votingInformationChanged.year)) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'year'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.invalidYear',
            });
        }
        if (Number(votingInformationChanged.year) > new Date().getFullYear()) {
            ctx.addIssue({
                path: ['votingInformationChanged', 'year'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.votingInformationChanged.yearInFuture',
            });
        }
    }

    if (
        votingInformationChanged.previousFirstName &&
        !APHABETIC_REGEX.test(votingInformationChanged.previousFirstName)
    ) {
        ctx.addIssue({
            path: ['votingInformationChanged', 'previousFirstName'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }

    if (votingInformationChanged.previousLastName && !APHABETIC_REGEX.test(votingInformationChanged.previousLastName)) {
        ctx.addIssue({
            path: ['votingInformationChanged', 'previousLastName'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }

    if (votingInformationChanged.city && !CITY_REGEX.test(votingInformationChanged.city)) {
        ctx.addIssue({
            path: ['votingInformationChanged', 'city'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }
}

function helpWithFormValidation(helpWithForm: any, ctx: any) {
    const { name, address, phone, confirmation } = helpWithForm;
    const hasName = !!name && name.trim() !== '';
    const hasAddress = !!address && address.trim() !== '';
    const hasPhone = !!phone && phone.trim() !== '';
    if (confirmation) {
        if (!hasName) {
            ctx.addIssue({
                path: ['helpWithForm', 'name'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.helpWithForm.nameRequired',
            });
        }
        if (!hasAddress) {
            ctx.addIssue({
                path: ['helpWithForm', 'address'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.helpWithForm.addressRequired',
            });
        }
        if (!hasPhone) {
            ctx.addIssue({
                path: ['helpWithForm', 'phone'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.helpWithForm.phoneRequired',
            });
        }
    }

    if (hasName) {
        if (!APHABETIC_REGEX.test(name)) {
            ctx.addIssue({
                path: ['helpWithForm', 'name'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.invalidCharacters',
            });
        }
    }

    if ((hasName || hasAddress || hasPhone) && !confirmation) {
        ctx.addIssue({
            path: ['helpWithForm', 'confirmation'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.helpWithForm.confirmationRequired',
        });
    }

    if (hasPhone && !new RegExp(`^\\d{${VALIDATIONS.PHONE.max}}$`).test(phone)) {
        ctx.addIssue({
            path: ['helpWithForm', 'phone'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.phone.invalid',
        });
    }
}

function pollWorkerValidation(pollWorker: any, ctx: any) {
    if (pollWorker.isBilingual) {
        if (!pollWorker.language || !pollWorker.language.trim()) {
            ctx.addIssue({
                path: ['pollWorker', 'language'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.pollWorker.languageRequired',
            });
        }
    }

    if (pollWorker.language && !APHABETIC_REGEX.test(pollWorker.language)) {
        ctx.addIssue({
            path: ['pollWorker', 'language'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }
}

function declarationValidation(declaration: boolean, ctx: any) {
    if (!declaration) {
        ctx.addIssue({
            path: ['declaration'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.declaration.required',
        });
    }
}

function fileSignatureValidation(file: any, identification: any, ctx: any) {
    const hasDL = !!identification.driverLicenseOrPennDotId && identification.driverLicenseOrPennDotId.trim() !== '';

    // Signature file is required only if applicant does NOT have a PA Driver's License/PennDOT ID
    if (!hasDL && !file) {
        ctx.addIssue({
            path: ['file'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.file.signatureRequired',
        });
    }
}

const addressSchema = (addressType: ADDRESS_TYPE) =>
    z.object({
        addressLine: z
            .string()
            .max(VALIDATIONS.ADDRESS_LINE.max, { message: 'voterRegistration.form.error.address.addressLineMax' })
            .optional(),
        ...(addressType === ADDRESS_TYPE.RESIDENTIAL
            ? {
                addressLine2: z
                    .string()
                    .max(VALIDATIONS.ADDRESS_LINE.max, { message: 'voterRegistration.form.error.address.addressLineMax' })
                    .optional(),
                unitNo: z
                    .string()
                    .max(VALIDATIONS.UNIT_NO.max, { message: 'voterRegistration.form.error.address.unitNoMax' })
                    .optional(),
                unitType: z.string().optional(),
                county: z.string().optional(),
            }
            : {}),
        city: z
            .string()
            .max(VALIDATIONS.CITY.max, { message: 'voterRegistration.form.error.address.cityMax' })
            .optional(),
        state: z.string().optional(),
        zipCode: z.string().optional(),
    });

function validateAddress(address, path: ADDRESS_TYPE, ctx) {
    const requiredFields = [
        { key: 'addressLine', message: 'voterRegistration.form.error.address.addressLineRequired' },
        { key: 'city', message: 'voterRegistration.form.error.address.cityRequired' },
        { key: 'state', message: 'voterRegistration.form.error.address.stateRequired' },
        { key: 'zipCode', message: 'voterRegistration.form.error.address.zipCodeRequired' },
    ];

    if (path === ADDRESS_TYPE.RESIDENTIAL) {
        requiredFields.push({ key: 'county', message: 'voterRegistration.form.error.address.countyRequired' });
    }

    requiredFields.forEach(field => {
        if (!address[field.key] || address[field.key].trim() === '') {
            ctx.addIssue({
                path: [path, field.key],
                code: ZodIssueCode.custom,
                message: field.message,
            });
        }
    });

    if (address.city && !CITY_REGEX.test(address.city)) {
        ctx.addIssue({
            path: [path, 'city'],
            code: ZodIssueCode.custom,
            message: 'voterRegistration.form.error.invalidCharacters',
        });
    }

    if (address.zipCode) {
        if (path === ADDRESS_TYPE.MAILING) {
            const zipCodeRegEx = /^(\d{5})(?:[-\s]\d{4})?$/;
            if (!zipCodeRegEx.test(address.zipCode)) {
                ctx.addIssue({
                    path: [path, 'zipCode'],
                    code: ZodIssueCode.custom,
                    message: 'voterRegistration.form.error.address.invalidMailingZipCode',
                });
            }
        } else if (
            address.zipCode.length !== VALIDATIONS.ZIP.max ||
            !new RegExp(`^\\d{${VALIDATIONS.ZIP.max}}$`).test(address.zipCode)
        ) {
            ctx.addIssue({
                path: [path, 'zipCode'],
                code: ZodIssueCode.custom,
                message: 'voterRegistration.form.error.address.invalidZipCode',
            });
        }
    }
}

function addressValidation(residentialAddress, mailingAddress, ctx) {
    validateAddress(residentialAddress, ADDRESS_TYPE.RESIDENTIAL, ctx);
    if (mailingAddress.isMailingSameAsResidential) return;
    validateAddress(mailingAddress, ADDRESS_TYPE.MAILING, ctx);
}

export const voterRegistrationFormSchema = z
    .object({
        firstName: z
            .string()
            .min(VALIDATIONS.FIRST_NAME.min, { message: 'voterRegistration.form.error.firstName.required' })
            .max(VALIDATIONS.FIRST_NAME.max, { message: 'voterRegistration.form.error.firstName.max' })
            .regex(APHABETIC_REGEX, {
                message: 'voterRegistration.form.error.invalidCharacters',
            }),
        lastName: z
            .string()
            .min(VALIDATIONS.LAST_NAME.min, { message: 'voterRegistration.form.error.lastName.required' })
            .max(VALIDATIONS.LAST_NAME.max, { message: 'voterRegistration.form.error.lastName.max' })
            .regex(APHABETIC_REGEX, {
                message: 'voterRegistration.form.error.invalidCharacters',
            }),
        middleName: z
            .string()
            .max(VALIDATIONS.MIDDLE_NAME.max, { message: 'voterRegistration.form.error.middleName.max' })
            .optional()
            .refine(val => !val || APHABETIC_REGEX.test(val), {
                message: 'voterRegistration.form.error.invalidCharacters',
            }),
        suffix: z.string().optional(),
        isUSCitizen: z
            .boolean()
            .nullable()
            .refine(val => val !== null, { message: 'voterRegistration.form.error.isUSCitizen.required' }),
        isAgeAbove18yr: z
            .boolean()
            .nullable()
            .refine(val => val !== null, { message: 'voterRegistration.form.error.isAgeAbove18yr.required' }),
        registrationReason: z
            .array(z.nativeEnum(RegistrationReason))
            .min(1, { message: 'voterRegistration.form.error.registrationReason.min' }),
        dateOfBirth: z
            .string()
            .refine(val => !isNaN(Date.parse(val)), {
                message: 'voterRegistration.form.error.dateOfBirth.required',
            })
            .refine(
                val => {
                    const date = new Date(val);
                    return date.getFullYear() >= VALIDATIONS.VOTING_INFO_CHANGED_YEAR.min;
                },
                { message: 'voterRegistration.form.error.dateOfBirth.before1900' },
            )
            .refine(
                val => {
                    const date = new Date(val);
                    return date.getFullYear() <= new Date().getFullYear();
                },
                { message: 'voterRegistration.form.error.dateOfBirth.future' },
            ),
        gender: z.string().optional(),
        race: z.string().optional(),
        primaryPhone: z.string().optional(),
        primaryEmail: z
            .string()
            .email({ message: 'voterRegistration.form.error.primaryEmail.invalid' })
            .or(z.literal(''))
            .optional(),
        residentialAddress: addressSchema(ADDRESS_TYPE.RESIDENTIAL),
        mailingAddress: z
            .object({
                isMailingSameAsResidential: z.boolean(),
            })
            .merge(addressSchema(ADDRESS_TYPE.MAILING)),
        identification: z.object({
            driverLicenseOrPennDotId: z
                .string()
                .max(VALIDATIONS.DRIVER_LICENSE.max, {
                    message: 'voterRegistration.form.error.identification.driverLicenseOrPennDotIdMax',
                })
                .optional(),
            last4digitsSSN: z
                .string()
                .max(VALIDATIONS.SSN4.max, { message: 'voterRegistration.form.error.identification.last4digitsSSNMax' })
                .optional(),
            driverLicenseOrSSNNotAvailable: z.boolean(),
        }),
        politicalParty: z.string().nullable().optional(),
        otherPoliticalParty: z
            .string()
            .max(VALIDATIONS.OTHER_PARTY.max, {
                message: 'voterRegistration.form.error.politicalParty.otherPartyMax',
            })
            .optional(),
        votingAssistance: z.object({
            helpRequired: z.boolean().nullable().optional(),
            kindOfAssistance: z.string().optional(),
            preferredLanguage: z
                .string()
                .max(VALIDATIONS.LANGUAGE.max, {
                    message: 'voterRegistration.form.error.votingAssistance.preferredLanguageMax',
                })
                .optional(),
        }),
        votingInformationChanged: z.object({
            registrationNumber: z
                .string()
                .max(VALIDATIONS.REGISTRATION_NUMBER.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.registrationNumberMax',
                })
                .optional(),
            previousFirstName: z
                .string()
                .max(VALIDATIONS.FIRST_NAME.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.previousFirstNameMax',
                })
                .optional(),
            previousLastName: z
                .string()
                .max(VALIDATIONS.LAST_NAME.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.previousLastNameMax',
                })
                .optional(),
            address: z
                .string()
                .max(VALIDATIONS.VOTING_INFO_CHANGED_ADDRESS.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.addressMax',
                })
                .optional(),
            city: z
                .string()
                .max(VALIDATIONS.VOTING_INFO_CHANGED_CITY.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.cityMax',
                })
                .optional(),
            state: z
                .string()
                .max(VALIDATIONS.VOTING_INFO_CHANGED_STATE.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.stateMax',
                })
                .optional(),
            zip: z
                .string()
                .max(VALIDATIONS.VOTING_INFO_CHANGED_ZIP.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.zipMax',
                })
                .optional(),
            county: z
                .string()
                .max(VALIDATIONS.VOTING_INFO_CHANGED_COUNTY.max, {
                    message: 'voterRegistration.form.error.votingInformationChanged.countyMax',
                })
                .optional(),
            year: z.string().optional(),
        }),
        helpWithForm: z.object({
            name: z
                .string()
                .max(VALIDATIONS.HELP_WITH_FORM_NAME.max, {
                    message: 'voterRegistration.form.error.helpWithForm.nameMax',
                })
                .optional(),
            address: z
                .string()
                .max(VALIDATIONS.HELP_WITH_FORM_ADDRESS.max, {
                    message: 'voterRegistration.form.error.helpWithForm.addressMax',
                })
                .optional(),
            phone: z
                .string()
                .max(VALIDATIONS.HELP_WITH_FORM_PHONE.max, {
                    message: 'voterRegistration.form.error.helpWithForm.phoneMax',
                })
                .optional(),
            confirmation: z.boolean(),
        }),
        pollWorker: z.object({
            isWorker: z.boolean().nullable().optional(),
            isBilingual: z.boolean().nullable().optional(),
            language: z
                .string()
                .max(VALIDATIONS.LANGUAGE.max, { message: 'voterRegistration.form.error.pollWorker.languageMax' })
                .optional(),
        }),
        transferRequest: z.boolean(),
        declaration: z.boolean().optional(),
        file: z.any().nullable().optional(),
    })
    .superRefine((data, ctx) => {
        validateValues(data, ctx);
        validateRegistrationReason(data.registrationReason || [], ctx);
        addressValidation(data.residentialAddress, data.mailingAddress, ctx);
        identificationValidation(data.identification, ctx);
        politicalPartyValidation(data.politicalParty, data.otherPoliticalParty, ctx);
        votingAssistanceValidation(data.votingAssistance, ctx);
        votingInformationChangedValidation(data.votingInformationChanged, data.registrationReason, ctx);
        helpWithFormValidation(data.helpWithForm, ctx);
        pollWorkerValidation(data.pollWorker, ctx);
        declarationValidation(!!data.declaration, ctx);
        fileSignatureValidation(data.file, data.identification, ctx);
    });

export const voterRegistratationDefaultValues = {
    firstName: '',
    lastName: '',
    middleName: '',
    suffix: '',
    isUSCitizen: null,
    isAgeAbove18yr: null,
    registrationReason: [],
    dateOfBirth: '',
    gender: '',
    race: '',
    primaryPhone: '',
    primaryEmail: '',
    residentialAddress: {
        addressLine: '',
        addressLine2: '',
        unitNo: '',
        unitType: '',
        city: '',
        state: 'PA',
        zipCode: '',
        county: '',
    },
    mailingAddress: {
        isMailingSameAsResidential: false,
        addressLine: '',
        city: '',
        state: '',
        zipCode: '',
    },
    identification: {
        driverLicenseOrPennDotId: '',
        last4digitsSSN: '',
        driverLicenseOrSSNNotAvailable: false,
    },
    politicalParty: null,
    otherPoliticalParty: '',
    votingAssistance: {
        helpRequired: null,
        kindOfAssistance: '',
        preferredLanguage: '',
    },
    votingInformationChanged: {
        registrationNumber: '',
        previousFirstName: '',
        previousLastName: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        county: '',
        year: '',
    },
    helpWithForm: {
        name: '',
        address: '',
        phone: '',
        confirmation: false,
    },
    pollWorker: {
        isWorker: null,
        isBilingual: null,
        language: '',
    },
    transferRequest: false,
    declaration: false,
    file: null,
};
